import { APPROVED, YES, WORKFLOW_RERUN } from "const"
import {
    CcReviewReviewItem,
    CcReviewReviewHistory,
    MtDriveFile,
} from "server/models"
import {
    LangType,
    CcApprovalLevelList,
    CcResponseList,
    CcReviewReviewTypeList,
    ReviewReviewRevisionActiveList,
    ReviewReviewStateList,
    ReviewReviewHistoryList,
    ReviewReviewHistoryNestedList,
    UserList,
    createFileName,
    getFile,
} from "client/store"
import { HistoryListPdfExport } from "client/components"

import * as Const from "client/components/pdfmake/template/const"

const detailsMargin = [0, 0, 0, 5]
const statusMargin = [0, 0, 0, 20]
const historyMargin = [20, 0, 0, 60]

const ReviewPage = (
    reviewData: { [fileId: number]: CcReviewReviewItem[] },
    revisionList: ReviewReviewRevisionActiveList,
    reviewStateList: ReviewReviewStateList,
    reviewHistoryList: ReviewReviewHistoryList,
    reviewHistoryNestedList: ReviewReviewHistoryNestedList,
    reviewTypes: CcReviewReviewTypeList,
    reviewDocuments: MtDriveFile[],
    levelList: CcApprovalLevelList,
    levelIds: number[],
    responseList: CcResponseList,
    userList: UserList,
    lang: LangType,
) => {
    const content: any[] = [
        {
            pageBreak: "before",
            style: Const.HEADER,
            text: `3.   ${lang.review}`,
            ...Const.TOC_HEADER_BASE,
        },
    ]

    reviewDocuments.forEach((file, index) => {
        content.push({
            style: Const.SUB_HEADER,
            text: `3.${index + 1} ${lang.document}: ${createFileName(file)}`,
            ...Const.TOC_ITEM_BASE,
        })
        if (!reviewData[file.id] || !reviewData[file.id].length) {
            content.push(
                {
                    italics: true,
                    text: lang.noReviewItems,
                },
                {
                    stack: [],
                    margin: historyMargin,
                },
            )
        } else {
            reviewData[file.id].forEach((item) => {
                const revision = revisionList[item.id]
                const itemState = reviewStateList[item.id] || null
                const itemHistory = reviewHistoryList[item.id] || []
                let lastHistory: CcReviewReviewHistory = null

                // get the last history row made by user, not by 'system'
                for (let i = itemHistory.length - 1; i >= 0; i--) {
                    const history = itemHistory[i]
                    if (history.workflow === WORKFLOW_RERUN) {
                        break
                    }
                    if (!history.systemMessage) {
                        lastHistory = history
                        break
                    }
                }

                // same as in review list columns
                const level = itemState ? levelList[itemState.levelId] : null
                const verifier = itemState ? userList[itemState.verifierId] : null

                content.push(
                    {
                        text: `${lang.remark}: ${item.remark}`,
                        lineHeight: 1.25,
                        alignment: "justify",
                        margin: [0, 0, 0, 2],
                    },
                    {
                        text: `${lang.seePage}: ${item.page}`,
                        margin: detailsMargin,
                    },
                    {
                        text: `${lang.kind}: ${reviewTypes[item.typeId] ? reviewTypes[item.typeId].name : ""}`,
                        margin: detailsMargin,
                    },
                    {
                        text: [
                            { text: `${lang.revision}: ` },
                            {
                                text: item.version ? item.version.revision : "",
                                color: "blue",
                                decoration: "underline",
                                link: getFile(file, item.version).download,
                            },
                        ],
                        margin: detailsMargin,
                    },
                    {
                        text: `${lang.reviewRevision}: ${revision.name}`,
                        margin: detailsMargin,
                    },
                )

                if (lastHistory && lastHistory.nextStatus === APPROVED) {
                    const response = responseList[lastHistory.responseId]
                    content.push({
                        text: `${lang.status}: ${response && response.approved === YES ? lang.approved : lang.notApproved}`,
                        margin: statusMargin,
                    })
                } else if (level) {
                    const firstLevel = level.id === levelIds[0]
                    const verifierName = verifier ? `${verifier.firstName} ${verifier.lastName}` : lang.notAssigned
                    content.push(
                        {
                            text: `${level.name}: ${verifierName}`,
                            margin: detailsMargin,
                        },
                        {
                            text: `${lang.status}: ${firstLevel ? lang.open : lang.verification}`,
                            margin: statusMargin,
                        },
                    )
                } else if (!level && lastHistory) { // approval level was removed or not set
                    content.push(
                        {
                            text: `${lang.status}: ${lang.verification}`,
                            margin: statusMargin,
                        },
                    )
                } else if (!level && !lastHistory) { // no any data
                    content.push(
                        {
                            text: `${lang.status}: ${lang.open}`,
                            margin: statusMargin,
                        },
                    )
                }

                // review history
                const historyNested = reviewHistoryNestedList[item.id]
                if (historyNested.length > 0) {
                    // table wrapper around an item history is required to keep the page structure
                    content.push({
                        layout: Const.ITEM_HISTORY_TABLE_LAYOUT,
                        table: {
                            widths: ["*"],
                            body: [
                                [{ stack: HistoryListPdfExport(historyNested, userList, levelList, responseList, lang) }],
                            ],
                        },
                        margin: historyMargin,
                    })
                }
            })
        }
    })

    return content
}
export default ReviewPage
